import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { getRequiredBookingConfirmationValue } from 'entities/kyrgyzstan/lib'

import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'
import { Paper } from 'shared/ui/paper'
import { Switch } from 'shared/ui/switch'

import type { CreateObjectDraftStepScreenProps } from '../../types'
import { CreateObjectStepLayout } from '../create-object-step-layout'

import { ConfirmationStepSkeleton } from './ui/confirmation-step-skeleton'

import s from './styles.module.scss'

type ConfirmationStepOperation = 'idle' | 'loading' | 'submitting'

export const ConfirmationStep = ({
  draftId,
  layoutProps,
  onNext,
}: CreateObjectDraftStepScreenProps) => {
  const { t } = useTranslation()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [operation, setOperation] = useState<ConfirmationStepOperation>('idle')
  const bookingConfirmed = getRequiredBookingConfirmationValue()

  useEffect(() => {
    const abortController = new AbortController()

    const fetchBookingStep = async () => {
      setOperation('loading')

      try {
        await KyrgyzstanCreateObjectApi.getBookingStep(
          draftId,
          abortController.signal,
        )
      } catch (error) {
        if (!abortController.signal.aborted) {
          showApiErrorDialog(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setOperation('idle')
        }
      }
    }

    fetchBookingStep()

    return () => {
      abortController.abort()
    }
  }, [draftId, showApiErrorDialog])

  const handleContinue = async () => {
    if (operation !== 'idle') {
      return
    }

    setOperation('submitting')

    try {
      await KyrgyzstanCreateObjectApi.saveBookingStep(draftId, {
        booking_confirmed: getRequiredBookingConfirmationValue(),
      })
      await KyrgyzstanCreateObjectApi.submitDraft(draftId)

      onNext()
    } catch (error) {
      setOperation('idle')
      showApiErrorDialog(error)
    }
  }

  const isOperationActive = operation !== 'idle'

  return (
    <CreateObjectStepLayout
      {...layoutProps}
      continueDisabled={isOperationActive}
      continueLoading={operation === 'submitting'}
      onContinue={handleContinue}
      title={t('kyrgyzstan_block.create_object.steps.confirmation.title')}
    >
      <div className={clsx(s.container, 'container')}>
        {operation === 'loading' ? (
          <ConfirmationStepSkeleton />
        ) : (
          <Paper
            padding={[12]}
            className={s.paper}
          >
            <p className={clsx(s.title, 'bold-16')}>
              {t('kyrgyzstan_block.create_object.steps.confirmation.helper')}
            </p>

            <Switch
              isChecked={bookingConfirmed}
              disabled
            />
          </Paper>
        )}
      </div>
    </CreateObjectStepLayout>
  )
}
